import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { api } from "../../../../api";
import { useAccountIds } from "../../../../auth/accessToken/components/withAccountIds";
import useHasAnyOfScopes from "../../../../common/hooks/useHasAnyOfScopes";
import { CORE_API_HOSTNAME } from "../../../../env";
import type {
    ApprovalsPayment,
    ApprovalsPaymentResponse,
} from "../../../../types/management-auth/generated";

type UsePaymentApprovalsOptions = {
    forceCheck?: boolean;
};

const fetchPaymentApprovals = async (
    payload: {
        accountId: string;
    } & UsePaymentApprovalsOptions,
): Promise<ApprovalsPaymentResponse[]> => {
    const response = await api<{ payments: ApprovalsPaymentResponse[] }>({
        accountId: payload.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${payload.accountId}/management/settings/approvals/payments${payload.forceCheck ? "?force_check=true" : ""}`,
    });
    return response.payments;
};

const fetchPostPaymentApproval = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            approval: ApprovalsPayment;
        };
    },
): Promise<ApprovalsPaymentResponse> => {
    return await api<ApprovalsPaymentResponse, ApprovalsPayment>({
        method: "POST",
        accountId: options.arg.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.accountId}/management/settings/approvals/payments?quickr_version=2`,
        json: options.arg.approval,
    });
};

export const PAYMENT_APPROVALS_WRITE_SCOPES = [
    "admin:accounts",
    "write:accounts",
    "write:accounts:/management/settings/approvals",
];

export const usePaymentApprovals = (options?: UsePaymentApprovalsOptions) => {
    const { prodAccountId } = useAccountIds();
    const canRead = useHasAnyOfScopes(["admin:accounts", "read:accounts"]);
    const canWrite = useHasAnyOfScopes(PAYMENT_APPROVALS_WRITE_SCOPES);
    const key = `${prodAccountId}/payment-approvals`;
    const {
        data: approvals,
        isLoading,
        error: approvalsError,
    } = useSWR(
        () => (prodAccountId && canRead ? key : null),
        async (key) => {
            const [accountId] = key.split("/");
            return await fetchPaymentApprovals({
                accountId,
                ...options,
            });
        },
        {
            revalidateOnFocus: true,
            revalidateIfStale: true,
            // Consumers of this key mount at different times during a page
            // load and not all of them ask for the same force_check, so a
            // short window would let a late mount re-fetch the approvals.
            dedupingInterval: 30_000,
        },
    );
    const {
        trigger: postApproval,
        isMutating: isPosting,
        error: postApprovalError,
        data: postApprovalResponse,
    } = useSWRMutation(
        () => (prodAccountId && canWrite ? key : null),
        fetchPostPaymentApproval,
    );

    return {
        approvals: approvals || [],
        isLoading,
        approvalsError,
        postApproval,
        isPosting,
        postApprovalError,
        posted: !!postApprovalResponse,
    };
};
